<template>
  <div class="p-4 sm:p-8 min-h-screen h-full bg-black font-mono text-sm uppercase text-warm-gray-500">

    <!-- QR Code Switcher -->
    <div class="flex flex-col lg:flex-row justify-between items-start gap-2 mt-2">

      <div v-if="!isWaiting" class="w-full border border-warm-gray-800 px-3 pt-2 pb-2 flex flex-col gap-2">
        <div class="flex justify-between items-center gap-2">
          <h1>Online<span class="hidden sm:inline"> Users</span>: <span
              :class="[onlineUsers.length > 0 ? 'text-green-500' : 'text-warm-gray-500']"> {{ onlineUsers.length }}</span></h1>
          <button @click="isOnlineUsersTableToggled = !isOnlineUsersTableToggled">
            <ChevronUpIcon v-if="isOnlineUsersTableToggled" class="w-4 h-4" />
            <ChevronDownIcon v-else class="w-4 h-4" />
          </button>
        </div>
        <div v-if="isOnlineUsersTableToggled">
          <div v-if="onlineUsers.length === 0" class="py-4 text-center">
            <p class="text-warm-gray-500">No online users</p>
          </div>
          <div v-else class="overflow-x-auto mt-2">
            <table class="min-w-full divide-y divide-warm-gray-800">
              <thead>
                <tr>
                  <th scope="col" class="px-3 py-2 text-left text-xs font-medium text-warm-gray-400 uppercase tracking-wider">
                    <!-- UID -->
                  </th>
                  <th scope="col" class="px-3 py-2 text-left text-xs font-medium text-warm-gray-400 uppercase tracking-wider">
                    Email
                  </th>
                  <th scope="col" class="px-3 py-2 text-left text-xs font-medium text-warm-gray-400 uppercase tracking-wider">
                    Name
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-warm-gray-800">
                <tr v-for="user in onlineUsers" :key="user.uid" class="hover:bg-warm-gray-900 hover:bg-opacity-50">
                  <td class="px-3 py-2 whitespace-nowrap">
                    <div class="flex items-center gap-2">
                      <div class="w-2 h-2 bg-green-500 rounded-full"></div>
                      <!-- <span class="text-warm-gray-400 text-xs font-mono">{{ user.uid || 'N/A' }}</span> -->
                    </div>
                  </td>
                  <td class="px-3 py-2 whitespace-nowrap">
                    <span class="text-warm-gray-400 text-xs normal-case">{{ user.email || 'N/A' }}</span>
                  </td>
                  <td class="px-3 py-2 whitespace-nowrap">
                    <span class="text-warm-gray-400 text-xs normal-case">
                      {{ (user.firstname && user.lastname) ? `${user.firstname} ${user.lastname}` : (user.firstname || user.lastname || 'N/A') }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div v-else class="w-full border border-warm-gray-800 px-3 py-2">
        <h1>Fetching Online Users...</h1>
      </div>

      <div v-if="!isWaiting" class="w-full border border-warm-gray-800 px-3 pt-2 pb-2 flex flex-col gap-2">
        <div class="flex justify-between items-center gap-2">
          <h1 v-if="!isWaiting"><span class="hidden sm:inline">Pending </span><span>Buyers</span>: <span
              :class="[pendingBuyersCount > 0 ? 'text-green-500' : 'text-warm-gray-500']"> {{ pendingBuyersCount
              }}</span></h1>
          <button v-if="computedPendingBuyers.length > 0" @click="isPendingBuyersToggled = !isPendingBuyersToggled">
            <ChevronUpIcon v-if="isPendingBuyersToggled" class="w-4 h-4" />
            <ChevronDownIcon v-else class="w-4 h-4" />
          </button>
        </div>
        <div v-if="isPendingBuyersToggled">
          <div v-if="currentUser" class="mb-1 border border-warm-gray-800 p-3">
            <div>
              <ul>
                <li>
                  Email: <span class="normal-case">{{ currentUser.email }}</span>
                </li>
                <li>
                  Reviewer: <span>{{ currentUser.indicator }}</span>
                </li>
                <li>
                  Purchase Date:
                  <span>
                    {{
                      currentUser.purchase_date && currentUser.purchase_date.seconds !== undefined
                        ? new Date(currentUser.purchase_date.seconds * 1000).toLocaleString()
                        : currentUser.purchase_date
                    }}
                  </span>
                </li>
                <li>
                  GCash Ref. No.: <span>{{ currentUser.gcash_refno }}</span>
                </li>
                <li>
                  Bundle Type: <span>{{ currentUser.bundleType || '-' }}</span>
                </li>
                <li>
                  Coupon Code: <span>{{ currentUser.couponCode || '-' }}</span>
                </li>
                <li>
                  Final Price: <span>{{ currentUser.finalPrice }}</span>
                </li>
                <li>
                  Duration: <span>{{ currentUser.durationInWords }}</span>
                </li>
                <li>
                  Proof of Payment: <span>{{ currentUser.proof_image_url }}</span>
                </li>
              </ul>
            </div>
          </div>
          <div v-if="computedPendingBuyers.length > 0" class="flex justify-between items-center gap-2 mt-2">
            <div class="flex justify-between items-center gap-2 mb-2">
              <button
                class="disabled:opacity-50 disabled:pointer-events-none disabled:cursor-default border border-warm-gray-800 px-2 py-2 uppercase"
                @click="activeUserIndex--" :disabled="activeUserIndex == 0">
                <ArrowLeftIcon class="w-4 h-4" />
              </button>
              <button
                class="disabled:opacity-50 disabled:pointer-events-none disabled:cursor-default border border-warm-gray-800 px-2 py-2"
                @click="activeUserIndex++" :disabled="activeUserIndex == computedPendingBuyers.length - 1">
                <ArrowRightIcon class="w-4 h-4" />
              </button>
            </div>
            <div>
              {{ activeUserIndex + 1 }} of {{ computedPendingBuyers.length }}
            </div>
            <button v-if="currentUser" :disabled="isConfirmingPurchase || isPurchaseConfirmed" :class="[
              'disabled:opacity-50 disabled:pointer-events-none disabled:cursor-default border px-3 py-1.5 uppercase',
              isPurchaseConfirmed ? 'border-green-500 text-green-500' : 'border-warm-gray-800'
            ]" @click="confirmUserPurchase(currentUser)">
              {{ isConfirmingPurchase ? 'CONFIRMING...' : isPurchaseConfirmed ? 'SUCCESSFUL' : 'CONFIRM' }}
            </button>
          </div>

        </div>

      </div>
      <div v-else class="border border-warm-gray-800 px-3 py-2 w-full">
        <h1>Fetching Pending Buyers...</h1>
      </div>

    </div>


    <!-- Pending Payouts & Pending Abox -->
    <div class="flex flex-col lg:flex-row justify-between items-start gap-2 mt-2">

      <div class="w-full border border-warm-gray-800 px-3 pt-2 pb-2 flex flex-col gap-2">
        <h1>Pending Payouts: <span :class="[pendingPayoutsCount > 0 ? 'text-green-500' : 'text-warm-gray-500']">{{ isWaiting ? '...' : pendingPayoutsCount }}</span></h1>
      </div>

      <div class="w-full border border-warm-gray-800 px-3 pt-2 pb-2 flex flex-col gap-2">
        <h1>Pending Abox: <span :class="[pendingAboxCount > 0 ? 'text-green-500' : 'text-warm-gray-500']">{{ isWaiting ? '...' : pendingAboxCount }}</span></h1>
      </div>

    </div>


    <!-- Active QR & Advisory Banner -->
    <div class="flex flex-col lg:flex-row justify-between items-start gap-2 mt-2">

      <div v-if="!isWaiting" class="border border-warm-gray-800 px-3 py-2 w-full">
        <div class="flex justify-between items-center gap-2">
          <h1>
            Active QR:
            <span class="text-green-500">
              {{
                (Array.isArray(qrCodes) && qrCodes.find(q => q.isActive))
                  ? qrCodes.find(q => q.isActive).storageFolderName
                  : 'None Active'
              }}
            </span>
          </h1>
          <button @click="isQRCodeSwitcherShowing = !isQRCodeSwitcherShowing">
            <ChevronUpIcon v-if="isQRCodeSwitcherShowing" class="w-4 h-4" />
            <ChevronDownIcon v-else class="w-4 h-4" />
          </button>
        </div>
        <div v-if="isQRCodeSwitcherShowing">
          <ul class="space-y-2 mt-2 mb-1">
            <li v-for="(qrCode, index) in qrCodes" :key="index">
              <button :disabled="updatingQRCode" @click="activateQRCode(index)"
                class="text-left px-3 py-2 border border-warm-gray-800 w-full hover:bg-warm-gray-900 hover:bg-opacity-50 transition-all duration-600 flex justify-between items-center disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none">
                <span>{{ qrCode.storageFolderName.toUpperCase() }} ({{ showLast4Digits(qrCode.gcashPhoneNum) }})</span>

                <div v-if="updatingQRCode" class="flex items-center gap-2">
                  <div class="w-3 h-3 bg-warm-gray-800 -mt-0.5"></div>
                  <span class="text-warm-gray-800 uppercase">Updating...</span>
                </div>
                <div v-else-if="qrCode.isActive" class="flex items-center gap-2">
                  <div class="w-3 h-3 bg-green-500 -mt-0.5"></div>
                  <span class="text-green-500 uppercase">Active</span>
                </div>
                <div v-else class="flex items-center gap-2">
                  <div class="w-3 h-3 bg-warm-gray-700 -mt-0.5"></div>
                  <span class="text-warm-gray-700 uppercase">Inactive</span>
                </div>
              </button>
            </li>
          </ul>
        </div>
      </div>
      <div v-else class="border border-warm-gray-800 px-3 py-2 w-full">
        <h1>Fetching QR codes...</h1>
      </div>


      <div v-if="!isWaiting" class="w-full border border-warm-gray-800 px-3 pt-2 pb-2 flex flex-col gap-2">
        <div class="flex justify-between items-center gap-2">
          <div class="flex items-center gap-2">
            <h1>
              Advisory Banner:
            </h1>
            <div v-if="isAdvisoryShowing" class="flex items-center gap-2">
              <div class="w-3 h-3 bg-green-500 -mt-0.5"></div>
            </div>
            <div v-else class="flex items-center gap-2">
              <div class="w-3 h-3 bg-warm-gray-800 -mt-0.5"></div>
            </div>
          </div>
          <button @click="isAdvisoryToggled = !isAdvisoryToggled">
            <ChevronUpIcon v-if="isAdvisoryToggled" class="w-4 h-4" />
            <ChevronDownIcon v-else class="w-4 h-4" />
          </button>
        </div>
        <div v-if="isAdvisoryToggled">
          <textarea :disabled="savingAdvisory" v-model="advisoryBanner"
            class="disabled:cursor-default disabled:pointer-events-none disabled:opacity-50 h-20 w-full flex-1 border border-warm-gray-800 text-xs px-3 py-2 bg-warm-gray-900 bg-opacity-50 resize-none outline-none focus:outline-none focus:ring-0 focus:border-warm-gray-800"
            style="min-height: 2.5rem; outline: none !important;" @focus="$event.target.style.outline = 'none'"
            @blur="$event.target.style.outline = 'none'"></textarea>
          <div class="flex justify-between items-center gap-2 mt-1 mb-1">
            <button :disabled="savingAdvisory" @click="toggleAdvisoryBanner()"
              class="text-left px-3 py-2 border border-warm-gray-800 w-full hover:bg-warm-gray-900 hover:bg-opacity-50 transition-all duration-600 flex justify-between items-center disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none whitespace-nowrap">

              <div v-if="savingAdvisory" class="flex items-center gap-2">
                <div class="w-3 h-3 bg-warm-gray-800 -mt-0.5"></div>
                <span class="text-warm-gray-800 uppercase">Updating...</span>
              </div>
              <div v-else-if="isAdvisoryShowing" class="flex items-center gap-2">
                <div class="w-3 h-3 bg-green-500 -mt-0.5"></div>
                <span class="text-green-500 uppercase">Active</span>
              </div>
              <div v-else class="flex items-center gap-2">
                <div class="w-3 h-3 bg-warm-gray-700 -mt-0.5"></div>
                <span class="text-warm-gray-700 uppercase">Inactive</span>
              </div>
            </button>
            <button @click="saveAdvisoryBanner" :disabled="!isAdvisoryEdited || savingAdvisory"
              class="disabled:cursor-default disabled:pointer-events-none text-left px-4 py-2 border border-warm-gray-800 hover:bg-warm-gray-900 hover:bg-opacity-50 transition-all duration-600 flex justify-between items-center disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap">
              <span>{{ savingAdvisory ? 'SAVING...' : 'SAVE CHANGES' }}</span>
            </button>
          </div>
        </div>

      </div>
      <div v-else class="border border-warm-gray-800 px-3 py-2 w-full">
        <h1>Fetching Advisory Banner...</h1>
      </div>

    </div>



  </div>
</template>
<script setup>
import { httpsCallable } from "firebase/functions";
import { ref as reffb, onValue, off } from "firebase/database";
import { rtdb, functions } from '@/firebase/config.js';
import { ref, onMounted, onBeforeUnmount, computed } from 'vue';
import { getDocs, onSnapshot, collection, orderBy, query, limit, where, doc, getDoc, updateDoc, setDoc, collectionGroup } from "firebase/firestore"
import { db } from '@/firebase/config.js';
import { ChevronUpIcon, ChevronDownIcon, ArrowLeftIcon, ArrowRightIcon } from "@heroicons/vue/24/outline";
const isWaiting = ref(true);
const pendingBuyers = ref({
  caap: [],
  caap_plus: [],
  mtp: [],
});
const pendingBuyersCount = computed(() => {
  return pendingBuyers.value.caap.length + pendingBuyers.value.caap_plus.length + pendingBuyers.value.mtp.length;
});
const onlineUsers = ref([]);
const isOnlineUsersTableToggled = ref(false);
const qrCodes = ref([]);
const isLoadingQRCodes = ref(false);
const updatingQRCode = ref(false);
const qrCodesDocPath = ref({ collection: null, docId: null }); // Store the document path for updates
const advisoryBanner = ref('');
const originalAdvisoryBanner = ref('');
const savingAdvisory = ref(false);
const isAdvisoryShowing = ref(false);
const isQRCodeSwitcherShowing = ref(false);
const isAdvisoryToggled = ref(false);
const isPendingBuyersToggled = ref(false);
const activeUserIndex = ref(0);
const isConfirmingPurchase = ref(false);
const isPurchaseConfirmed = ref(false);
// Store original unaltered buyer data by UUID
const originalBuyerData = ref(new Map());
// Store unsubscribe functions for pending buyers listeners
const pendingBuyersUnsubscribes = ref([]);

const pendingPayoutsCount = ref(0);
const pendingAboxCount = ref(0);
let unsubscribePendingPayouts = null;
let unsubscribePendingAbox = null;

// Computed property to check if advisory has been edited
const isAdvisoryEdited = computed(() => {
  return advisoryBanner.value !== originalAdvisoryBanner.value;
});

/**
 * I need to add an indicator for each user to what reviewer they are pending for.
 */
const computedPendingBuyers = computed(() => {
  // Helper function to process users from a specific purchase type
  const processUsers = (users, purchaseType) => {
    return users.map(user => {
      let dataField = null;
      let indicator = null;

      if (purchaseType === 'caap') {
        dataField = user.caap_data;
        indicator = 'CAAP Knowledge';
      } else if (purchaseType === 'caap_plus') {
        dataField = user.caap_plus_data;
        indicator = 'CAAP Practical';
      } else if (purchaseType === 'mtp') {
        dataField = user.mtp_data;
        indicator = 'MTP Entrance';
      }

      return {
        ...user,
        buyerId: user.buyerId, // Preserve UUID
        indicator: indicator,
        purchase_date: dataField ? dataField.purchase_date_v2 : null,
        gcash_refno: dataField ? dataField.gcash_refno : null,
        proof_image_url: dataField ? dataField.proof_image_url : null,
        isBundled: dataField ? dataField.isBundled : null,
        couponCode: dataField ? dataField.couponCode : null,
        finalPrice: dataField ? dataField.finalPrice : null,
        duration: dataField ? dataField.duration : null,
        durationInWords: dataField ? dataField.durationInWords : null,
        activationDate: dataField ? dataField.activationDate : null,
        expirationDate: dataField ? dataField.expirationDate : null,
        bundleType: dataField ? dataField.bundleType : null,
      }
    });
  };

  // Process each array separately, then concat
  const caapProcessed = processUsers(pendingBuyers.value.caap, 'caap');
  const caapPlusProcessed = processUsers(pendingBuyers.value.caap_plus, 'caap_plus');
  const mtpProcessed = processUsers(pendingBuyers.value.mtp, 'mtp');

  return caapProcessed.concat(caapPlusProcessed).concat(mtpProcessed);
});

/**
 * Computed property for the currently active user
 * Returns null if index is out of bounds
 */
const currentUser = computed(() => {
  if (computedPendingBuyers.value.length === 0) {
    return null;
  }
  // Ensure activeUserIndex is within bounds
  if (activeUserIndex.value >= computedPendingBuyers.value.length) {
    activeUserIndex.value = Math.max(0, computedPendingBuyers.value.length - 1);
  }
  return computedPendingBuyers.value[activeUserIndex.value] || null;
});

onMounted(async () => {
  isWaiting.value = true;
  try {
    setupPendingBuyersListeners();
    setupPendingPayoutsListener();
    setupPendingAboxListener();
    await getUsersPresence();
    await fetchQRCodes();
    await fetchAdvisoryBanner();
  } catch (error) {
    console.error(error);
  }
  isWaiting.value = false;
})

onBeforeUnmount(() => {
  stopListeningUsersPresence();
  pendingBuyersUnsubscribes.value.forEach(unsubscribe => unsubscribe());
  pendingBuyersUnsubscribes.value = [];
  if (unsubscribePendingPayouts) unsubscribePendingPayouts();
  if (unsubscribePendingAbox) unsubscribePendingAbox();
})

/**
 * Refetch pending buyers data after confirming a purchase
 */
const refetchData = async () => {
  try {
    pendingBuyers.value = await fetchPendingBuyers();
    // Reset active user index to 0 since the list has changed
    activeUserIndex.value = 0;
    isConfirmingPurchase.value = false;
    isPurchaseConfirmed.value = false;
  } catch (error) {
    console.error('Error refetching pending buyers:', error);
    isConfirmingPurchase.value = false;
    isPurchaseConfirmed.value = false;
  }
}

const showLast4Digits = (phoneNumber) => {
  return `09*****${phoneNumber.slice(-4)}`;
}

/**
 * Fetch user details from Firestore to enrich online users data
 * Uses parallel Promise.all() for efficiency
 */
const enrichOnlineUsersWithFirestoreData = async (onlineUsersList) => {
  if (onlineUsersList.length === 0) return [];
  
  try {
    // Fetch all user documents in parallel using Promise.all()
    const userPromises = onlineUsersList.map(async (user) => {
      try {
        const userDocRef = doc(db, "users", user.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          return {
            ...user,
            email: userData.email || 'N/A',
            firstname: userData.firstname || '',
            lastname: userData.lastname || '',
          };
        }
        return user;
      } catch (error) {
        console.error(`Error fetching user data for ${user.uid}:`, error);
        return user;
      }
    });

    return await Promise.all(userPromises);
  } catch (error) {
    console.error('Error enriching online users data:', error);
    return onlineUsersList;
  }
}

const getUsersPresence = async () => {
  const userAdminRef = reffb(rtdb, 'users-app');
  console.log('listening to app user presence change...')
    onValue(userAdminRef, async (snapshot) => {
    const onlineUsersList = [];
    snapshot.forEach(childSnapshot => {
      const userData = childSnapshot.val();
      if (userData.status == 'online') {
        onlineUsersList.push({
          uid: childSnapshot.key,
          status: userData.status,
        });
      }
    });
    
    // Enrich with Firestore data
    onlineUsers.value = await enrichOnlineUsersWithFirestoreData(onlineUsersList);
  })
}

const toggleAdvisoryBanner = async () => {
  if (savingAdvisory.value) {
    return;
  }
  savingAdvisory.value = true;

  try {
    const newIsShownValue = !isAdvisoryShowing.value;

    // Update local state first for immediate UI feedback
    isAdvisoryShowing.value = newIsShownValue;

    // Update in Firestore
    const advisoryDocRef = doc(db, "settings", "advisory");
    await updateDoc(advisoryDocRef, {
      isShown: newIsShownValue,
      updatedAt: new Date().toISOString()
    });

    console.log('Advisory banner visibility toggled successfully');
  } catch (error) {
    console.error('Error toggling advisory banner:', error);
    // Revert local state on error
    isAdvisoryShowing.value = !isAdvisoryShowing.value;
  } finally {
    savingAdvisory.value = false;
  }
}

const stopListeningUsersPresence = () => {
  console.log('stop listening to app user presence change...')
  const userAdminRef = reffb(rtdb, 'users-app');
  off(userAdminRef);
}

/**
 * Helper function to process snapshot and update buyer data
 */
const processBuyerSnapshot = (querySnapshot, indicator) => {
  return querySnapshot.docs.map(doc => {
    const userData = doc.data();
    // Generate unique UUID for this buyer entry
    const buyerId = crypto.randomUUID();
    // Store original unaltered data with document ID
    const originalData = { ...userData, id: doc.id };
    originalBuyerData.value.set(buyerId, JSON.parse(JSON.stringify(originalData)));
    // Add UUID and document ID to user data
    return { ...userData, id: doc.id, buyerId, indicator };
  });
}

/**
 * Set up real-time listeners for pending buyers
 */
const setupPendingBuyersListeners = () => {
  // Clear previous data
  originalBuyerData.value.clear();

  // Clear any existing listeners
  pendingBuyersUnsubscribes.value.forEach(unsubscribe => unsubscribe());
  pendingBuyersUnsubscribes.value = [];

  // CAAP buyers listener
  const caapQuery = query(
    collection(db, "users"),
    orderBy("date_created", "desc"),
    limit(100),
    where("reviewers.caap", "==", "paid")
  );

  const unsubscribeCaap = onSnapshot(caapQuery, (querySnapshot) => {
    pendingBuyers.value.caap = processBuyerSnapshot(querySnapshot, 'CAAP Knowledge');
  }, (error) => {
    console.error('Error listening to CAAP buyers:', error);
  });

  // CAAP Plus buyers listener
  const caapPlusQuery = query(
    collection(db, "users"),
    orderBy("date_created", "desc"),
    limit(100),
    where("reviewers.caap_plus", "==", "paid")
  );

  const unsubscribeCaapPlus = onSnapshot(caapPlusQuery, (querySnapshot) => {
    pendingBuyers.value.caap_plus = processBuyerSnapshot(querySnapshot, 'CAAP Practical');
  }, (error) => {
    console.error('Error listening to CAAP Plus buyers:', error);
  });

  // MTP buyers listener
  const mtpQuery = query(
    collection(db, "users"),
    orderBy("date_created", "desc"),
    limit(100),
    where("reviewers.mtp", "==", "paid")
  );

  const unsubscribeMtp = onSnapshot(mtpQuery, (querySnapshot) => {
    pendingBuyers.value.mtp = processBuyerSnapshot(querySnapshot, 'MTP Entrance');
  }, (error) => {
    console.error('Error listening to MTP buyers:', error);
  });

  // Store unsubscribe functions
  pendingBuyersUnsubscribes.value = [unsubscribeCaap, unsubscribeCaapPlus, unsubscribeMtp];
}

/**
 * Legacy function for one-time fetch (kept for refetchData compatibility)
 */
const fetchPendingBuyers = async () => {
  // Clear previous data
  originalBuyerData.value.clear();

  const caapBuyers = await getDocs(
    query(
      collection(db, "users"),
      orderBy("date_created", "desc"),
      limit(100),
      where("reviewers.caap", "==", "paid")
    )).then((querySnapshot) => {
      return processBuyerSnapshot(querySnapshot, 'CAAP Knowledge');
    });

  const caapPlusBuyers = await getDocs(
    query(
      collection(db, "users"),
      orderBy("date_created", "desc"),
      limit(100),
      where("reviewers.caap_plus", "==", "paid")
    )).then((querySnapshot) => {
      return processBuyerSnapshot(querySnapshot, 'CAAP Practical');
    });

  const mtpBuyers = await getDocs(
    query(
      collection(db, "users"),
      orderBy("date_created", "desc"),
      limit(100),
      where("reviewers.mtp", "==", "paid")
    )).then((querySnapshot) => {
      return processBuyerSnapshot(querySnapshot, 'MTP Entrance');
    });

  return {
    caap: caapBuyers,
    caap_plus: caapPlusBuyers,
    mtp: mtpBuyers,
  }
}

/**
 * Fetch QR codes from settings.qr_codes document
 */
const fetchQRCodes = async () => {
  isLoadingQRCodes.value = true;
  try {
    const qrCodesDocRef = doc(db, "settings", "qr_codes");
    const qrCodesDoc = await getDoc(qrCodesDocRef);

    if (qrCodesDoc.exists()) {
      const data = qrCodesDoc.data();
      // If the data has a 'list' field (array), use that
      if (data.list && Array.isArray(data.list)) {
        qrCodes.value = data.list;
        qrCodesDocPath.value = { collection: 'settings', docId: 'qr_codes' };
      }
    }
  } catch (error) {
    console.error('Error fetching QR codes:', error);
  }
  isLoadingQRCodes.value = false;
}

/**
 * Activate a specific QR code and deactivate all others
 */
const activateQRCode = async (index) => {
  if (updatingQRCode.value) return; // Prevent multiple simultaneous updates

  updatingQRCode.value = true;

  try {
    // Update local state first for immediate UI feedback
    const updatedList = qrCodes.value.map((qrCode, i) => ({
      ...qrCode,
      isActive: i === index
    }));
    qrCodes.value = updatedList;

    // Update in Firestore
    const qrCodesDocRef = doc(db, "settings", "qr_codes");
    await updateDoc(qrCodesDocRef, {
      list: updatedList
    });
    console.log('QR code activated successfully');
  } catch (error) {
    console.error('Error activating QR code:', error);
    // Revert local state on error
    await fetchQRCodes();
  } finally {
    updatingQRCode.value = false;
  }
}

/**
 * Fetch advisory banner from settings.advisory document
 */
const fetchAdvisoryBanner = async () => {
  try {
    const advisoryDocRef = doc(db, "settings", "advisory");
    const advisoryDoc = await getDoc(advisoryDocRef);

    if (advisoryDoc.exists()) {
      const data = advisoryDoc.data();
      const bannerText = data.text || '';
      advisoryBanner.value = bannerText;
      originalAdvisoryBanner.value = bannerText;
      isAdvisoryShowing.value = data.isShown || false;
    } else {
      // If document doesn't exist, initialize with empty string
      advisoryBanner.value = '';
      originalAdvisoryBanner.value = '';
      isAdvisoryShowing.value = false;
    }
  } catch (error) {
    console.error('Error fetching advisory banner:', error);
    advisoryBanner.value = '';
    originalAdvisoryBanner.value = '';
    isAdvisoryShowing.value = false;
  }
}

/**
 * Save advisory banner to Firestore
 */
const saveAdvisoryBanner = async () => {
  if (!isAdvisoryEdited.value || savingAdvisory.value) return;

  savingAdvisory.value = true;

  try {
    const advisoryDocRef = doc(db, "settings", "advisory");

    // Use setDoc with merge to create or update
    await setDoc(advisoryDocRef, {
      text: advisoryBanner.value,
      isShown: isAdvisoryShowing.value,
      updatedAt: new Date().toISOString()
    }, { merge: true });

    // Update original value after successful save
    originalAdvisoryBanner.value = advisoryBanner.value;
    console.log('Advisory banner saved successfully');
  } catch (error) {
    console.error('Error saving advisory banner:', error);
    // Optionally show error message to user
  } finally {
    savingAdvisory.value = false;
  }
}

const setupPendingPayoutsListener = () => {
  const q = query(collection(db, 'endorsers_payouts'), where('status', '==', 'requested'));
  unsubscribePendingPayouts = onSnapshot(q, (snapshot) => {
    pendingPayoutsCount.value = snapshot.size;
  }, (error) => {
    console.error('Error listening to pending payouts:', error);
  });
}

const setupPendingAboxListener = () => {
  unsubscribePendingAbox = onSnapshot(collection(db, 'claimants'), (snapshot) => {
    pendingAboxCount.value = snapshot.docs.filter(d => {
      const data = d.data();
      const abox = data.achievementBoxInfoStatus;
      const survey = data.postExamSurveyInfoStatus;
      return !data.isForfeited &&
        (abox === 'submitted' || survey === 'submitted') &&
        abox !== 'rejected' && survey !== 'rejected';
    }).length;
  }, (error) => {
    console.error('Error listening to pending abox:', error);
  });
}

/**
 * Get original unaltered buyer data by UUID
 */
const getOriginalBuyerData = (buyerId) => {
  return originalBuyerData.value.get(buyerId);
}

/**
 * Get reviewer expiry date (defaults to 180 days from now)
 * @param {number} days - Number of days to add (default: 180)
 */
const getReviewerExpiry = (days = 180) => {
  let durationInDays = days;
  const currentDate = new Date();
  currentDate.setDate(currentDate.getDate() + durationInDays);
  return currentDate;
}

/**
 * Confirm user purchase using UUID to get original unaltered data
 */
const confirmUserPurchase = async (computedUser) => {
  if (!computedUser.buyerId) {
    console.error('No buyerId found in user data');
    isConfirmingPurchase.value = false;
    return;
  }

  // Get original unaltered user data using UUID
  const originalUser = getOriginalBuyerData(computedUser.buyerId);

  if (!originalUser) {
    console.error('Original user data not found for buyerId:', computedUser.buyerId);
    isConfirmingPurchase.value = false;
    return;
  }

  // Use original data for confirmation
  await confirmUserPurchase2(originalUser, computedUser.indicator);
}

const confirmUserPurchase2 = async (user, indicator) => {

  console.log('User data for confirmation:', user);

  let rev = indicator;

  isConfirmingPurchase.value = true;
  /**
   * First step is to purchase the reviewer for the user via
   * firebase function. This is for setting the user claims
  */

  let funcPayload = {};
  let keysToremove = [];
  switch (rev) {
    case 'CAAP Knowledge':
      funcPayload = { caap: false };
      keysToremove.push('caapExpAt')
      break;
    case 'CAAP Practical':
      funcPayload = { caap_plus: false };
      keysToremove.push('plusExpAt')
      break;
    case 'MTP Entrance':
      funcPayload = { mtp: false };
      keysToremove.push('mtpExpAt')
      break;
  }

  console.log('test', {
    email: user.email,
    payload: funcPayload,
    toRemoveKeys: keysToremove,
  })

  const updateUserClaimsFbFunc = httpsCallable(functions, "updateUserClaims");
  const updateUserClaimsResponse = await updateUserClaimsFbFunc({
    email: user.email,
    payload: funcPayload,
    toRemoveKeys: keysToremove,
  })

  if (updateUserClaimsResponse.data.success) {

    /**
     * Update the User's Reviewer information
     */
    let _data;
    let _reviewers;
    let _user_rev_data;

    switch (rev) {
      case 'CAAP Knowledge':
        // update the caap_data
        _user_rev_data = user.caap_data;
        _user_rev_data.activateLater = _user_rev_data.activateLater ? _user_rev_data.activateLater : false;
        _user_rev_data.activation_date = (_user_rev_data.activateLater) ? null : new Date();
        _user_rev_data.expiration_date = (_user_rev_data.activateLater) ? null : getReviewerExpiry();
        _data = { caap_data: _user_rev_data };
        // update caap in reviewers object to ready
        _reviewers = {
          'reviewers.caap': 'ready'
        }

        break;

      case 'CAAP Practical':
        // update the caap_plus_data
        _user_rev_data = user.caap_plus_data;
        _user_rev_data.activateLater = true;
        _user_rev_data.activation_date = null;
        _user_rev_data.expiration_date = null;
        _data = { caap_plus_data: _user_rev_data };
        // update caap_lite in reviewers object to ready
        _reviewers = {
          'reviewers.caap_plus': 'ready'
        }
        break;
      case 'MTP Entrance':
        // update the mtp_data
        _user_rev_data = user.mtp_data;
        _user_rev_data.activateLater = true;
        _user_rev_data.activation_date = null;
        _user_rev_data.expiration_date = null;
        _data = { mtp_data: _user_rev_data };
        // update mtp in reviewers object to ready
        _reviewers = {
          'reviewers.mtp': 'ready'
        }
        break;
    }



    console.log(_reviewers, _data);

    updateDoc(doc(db, "users", user.id), { ..._reviewers, ..._data })
      .then(() => {
        console.log(`User's ${rev.toUpperCase()} information has been successfully updated.`)

        // Show success state
        isConfirmingPurchase.value = false;
        isPurchaseConfirmed.value = true;

        // Revert success state after 3 seconds
        setTimeout(() => {
          isPurchaseConfirmed.value = false;
        }, 3000);


        /**
         * Send confirmation email to the user
         */
        let reviewerName = '';
        let durationInWords = '';
        let originalAmount = 0;
        let discount = 0;
        let paidAmount = 0;
        let isBundled = false;
        let bundleDiscountAmount = 0;

        switch (rev) {
          case 'CAAP Knowledge':
            reviewerName = 'CAAP Knowledge';
            durationInWords = user.caap_data.durationInWords;
            discount = user.caap_data.couponDiscount ? parseFloat(user.caap_data.couponDiscount) : 0;
            isBundled = user.caap_data.isBundled ? true : false;
            paidAmount = parseFloat(user.caap_data.finalPrice);
            originalAmount = isBundled ? user.caap_data.originalPrice : parseFloat(user.caap_data.finalPrice) + discount;
            bundleDiscountAmount = isBundled ? user.caap_data.originalPrice - (parseFloat(user.caap_data.finalPrice) + discount) : 0;

            break;
          case 'CAAP Practical':
            reviewerName = 'CAAP Practical';
            durationInWords = user.caap_plus_data.durationInWords;
            discount = user.caap_plus_data.couponDiscount ? parseFloat(user.caap_plus_data.couponDiscount) : 0;
            isBundled = user.caap_plus_data.isBundled ? true : false;
            paidAmount = parseFloat(user.caap_plus_data.finalPrice);
            originalAmount = isBundled ? user.caap_plus_data.originalPrice : parseFloat(user.caap_plus_data.finalPrice) + discount;
            paidAmount = parseFloat(user.caap_plus_data.finalPrice);
            bundleDiscountAmount = isBundled ? user.caap_plus_data.originalPrice - (parseFloat(user.caap_plus_data.finalPrice) + discount) : 0;
            break;
          case 'MTP Entrance':
            reviewerName = 'Entrance Exam';
            durationInWords = user.mtp_data.durationInWords;
            discount = user.mtp_data.couponDiscount ? parseFloat(user.mtp_data.couponDiscount) : 0;
            originalAmount = parseFloat(user.mtp_data.finalPrice) + discount;
            paidAmount = parseFloat(user.mtp_data.finalPrice);
            break;
        }

        const sendInvoiceEmailFbFunc = httpsCallable(functions, "sendEmailConfirmPurchase");
        const sendInvoiceEmail = sendInvoiceEmailFbFunc({
          email: user.email,
          name: user.firstname,
          reviewerName: reviewerName,
          durationInWords: durationInWords,
          originalAmount: originalAmount,
          discount: discount,
          paidAmount: paidAmount,
          isBundled: isBundled,
          bundleDiscountAmount: bundleDiscountAmount
        })

        setTimeout(() => {
          refetchData();
        }, 2000);

      })
      .catch((error) => {
        console.error(`Error updating user ${rev.toUpperCase()} information: `, error);
        isConfirmingPurchase.value = false;
        isPurchaseConfirmed.value = false;
      });

  } else {
    console.error(`An error happened during ${rev.toUpperCase()} reviewer purchase.`)
    isConfirmingPurchase.value = false;
    isPurchaseConfirmed.value = false;
  }
}





</script>